/**
 * React Flow Schema Graph Component
 *
 * Interactive node-based visualization for Neo4j schema design using React Flow.
 * Replaces Mermaid ER diagrams with a more interactive experience:
 * - Drag and drop nodes
 * - Zoom and pan
 * - Click to select nodes/edges
 * - View node properties on hover/click
 * - Auto-layout using dagre
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  MarkerType,
  Panel,
  Position,
  ConnectionLineType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { SchemaNodeCard } from './SchemaNodeCard';
import { KGEntityNode } from './KGEntityNode';
import { SchemaEdgeLabel } from './SchemaEdgeLabel';
import { SchemaLegend } from './SchemaLegend';
import { AddRelationshipModal, NewRelationshipData } from './AddRelationshipModal';
import { AddNodeModal, NewNodeData } from './AddNodeModal';
import { EditNodeModal, EditNodeData } from './EditNodeModal';
import { EditRelationshipModal, EditRelationshipData } from './EditRelationshipModal';
import type { SchemaDesign, SchemaNode, SchemaRelationship } from '../../types/mapping';
import type { SchemaPreview, PreviewTable, PreviewRelationship } from '../../types/knowledgeGraphDesign';
import Button from '../ui/Button';
import { RefreshCw, Plus, Trash2, AlertTriangle, X, Edit3, Download, Search, Filter, Share2, LayoutGrid } from 'lucide-react';
import { getNodeColors } from '../../lib/graph/graphTheme';
import { computeForceLayout } from '../../lib/graph/forceLayout';
import { layoutWithDagre } from '../../lib/graph/dagreLayout';
import { exportElementToPng } from '../../lib/graph/graphExport';
import { buildAdjacency, neighborhoodOf, matchNodeIds, DIMMED_OPACITY } from '../../lib/graph/graphInteractions';

// Custom node types - includes both table-style and KG-style nodes
const nodeTypes = {
  schemaNode: SchemaNodeCard,   // Table/card style for legacy mode
  kgEntity: KGEntityNode,        // Circular style for KG-First mode
};

// Custom edge types
const edgeTypes = {
  schemaEdge: SchemaEdgeLabel,
};

interface ReactFlowSchemaGraphProps {
  // Can accept either SchemaDesign (quick mode) or SchemaPreview (advanced mode)
  schemaDesign?: SchemaDesign;
  schemaPreview?: SchemaPreview;
  onNodeClick?: (nodeId: string, node: SchemaNode | PreviewTable) => void;
  onEdgeClick?: (edgeId: string, edge: SchemaRelationship | PreviewRelationship) => void;
  /** Called when a new relationship is added */
  onRelationshipAdd?: (relationship: {
    sourceId: string;
    targetId: string;
    relationshipType: string;
    cardinality: string;
    description: string;
  }) => void;
  /** Called when a node is deleted */
  onNodeDelete?: (nodeId: string) => void;
  /** Called when an edge/relationship is deleted */
  onEdgeDelete?: (edgeId: string) => void;
  /** Called when a new node is added */
  onNodeAdd?: (nodeData: NewNodeData) => void;
  /** Called when a node is edited */
  onNodeEdit?: (nodeData: EditNodeData) => void;
  /** Called when an edge/relationship is edited */
  onEdgeEdit?: (edgeData: EditRelationshipData) => void;
  /** Enable editing mode (allows adding relationships) */
  editable?: boolean;
  height?: number | string;
  className?: string;
}

// Node colors come from the shared graph theme (src/lib/graph/graphTheme.ts) so
// this renderer, SchemaLegend and the vis-network SchemaVisualizer stay in sync.

// Auto-layout using category-based clustering algorithm
// Groups nodes by category and places disconnected nodes on the side
const calculateLayout = (
  nodes: Node[],
  edges: Edge[],
  width: number = 1600,
  height: number = 1200
): Node[] => {
  if (nodes.length === 0) return [];

  // Check if this is KG-First mode (circular nodes)
  const isKGFirst = nodes.some(n => n.type === 'kgEntity');

  // Calculate connection count for each node
  const connectionCount: Record<string, number> = {};
  edges.forEach(edge => {
    connectionCount[edge.source] = (connectionCount[edge.source] || 0) + 1;
    connectionCount[edge.target] = (connectionCount[edge.target] || 0) + 1;
  });

  // Separate connected and disconnected nodes
  const connectedNodes = nodes.filter(n => (connectionCount[n.id] || 0) > 0);
  const disconnectedNodes = nodes.filter(n => (connectionCount[n.id] || 0) === 0);

  // Group connected nodes by category/module
  const nodesByCategory: Record<string, Node[]> = {};
  connectedNodes.forEach(node => {
    const data = node.data as any;
    const category = data?.kgAreaName || data?.category || data?.module_name || 'Other';
    if (!nodesByCategory[category]) {
      nodesByCategory[category] = [];
    }
    nodesByCategory[category].push(node);
  });

  // Sort nodes within each category by connection count (most connected first)
  Object.keys(nodesByCategory).forEach(category => {
    nodesByCategory[category].sort((a, b) => {
      return (connectionCount[b.id] || 0) - (connectionCount[a.id] || 0);
    });
  });

  // Get category names sorted by total connections (most connected categories first)
  const categories = Object.keys(nodesByCategory).sort((a, b) => {
    const aConnections = nodesByCategory[a].reduce((sum, n) => sum + (connectionCount[n.id] || 0), 0);
    const bConnections = nodesByCategory[b].reduce((sum, n) => sum + (connectionCount[n.id] || 0), 0);
    return bConnections - aConnections;
  });

  const positionedNodes: Node[] = [];

  if (isKGFirst) {
    // KG-First layout: Category clusters arranged in a circle around center
    const mainAreaWidth = disconnectedNodes.length > 0 ? width * 0.75 : width;
    const centerX = mainAreaWidth / 2;
    const centerY = height / 2;

    // Place category clusters in a circular arrangement
    const numCategories = categories.length;
    const categoryRadius = Math.min(mainAreaWidth, height) * 0.35; // Distance from center to category center

    categories.forEach((category, categoryIndex) => {
      const categoryNodes = nodesByCategory[category];

      // Calculate category cluster center position
      let clusterCenterX: number;
      let clusterCenterY: number;

      if (numCategories === 1) {
        // Single category: center it
        clusterCenterX = centerX;
        clusterCenterY = centerY;
      } else {
        // Multiple categories: arrange in a circle
        const categoryAngle = (categoryIndex / numCategories) * 2 * Math.PI - Math.PI / 2;
        clusterCenterX = centerX + Math.cos(categoryAngle) * categoryRadius;
        clusterCenterY = centerY + Math.sin(categoryAngle) * categoryRadius;
      }

      // Position nodes within the category cluster
      const numNodesInCategory = categoryNodes.length;
      const nodeSpacing = 120; // Space between nodes within a cluster

      if (numNodesInCategory === 1) {
        // Single node in category
        positionedNodes.push({
          ...categoryNodes[0],
          position: { x: clusterCenterX - 40, y: clusterCenterY - 40 }
        });
      } else if (numNodesInCategory <= 4) {
        // Small cluster: arrange in a small circle
        const smallRadius = nodeSpacing * 0.7;
        categoryNodes.forEach((node, nodeIndex) => {
          const nodeAngle = (nodeIndex / numNodesInCategory) * 2 * Math.PI - Math.PI / 2;
          const x = clusterCenterX + Math.cos(nodeAngle) * smallRadius - 40;
          const y = clusterCenterY + Math.sin(nodeAngle) * smallRadius - 40;
          positionedNodes.push({ ...node, position: { x, y } });
        });
      } else {
        // Larger cluster: use expanding rings within the cluster
        categoryNodes.forEach((node, nodeIndex) => {
          if (nodeIndex === 0) {
            // Most connected node in center of cluster
            positionedNodes.push({
              ...node,
              position: { x: clusterCenterX - 40, y: clusterCenterY - 40 }
            });
          } else {
            const ring = Math.floor((nodeIndex - 1) / 6) + 1; // 6 nodes per ring
            const posInRing = (nodeIndex - 1) % 6;
            const nodesInRing = Math.min(6, numNodesInCategory - 1 - (ring - 1) * 6);
            const angle = (posInRing / nodesInRing) * 2 * Math.PI - Math.PI / 2;
            const radius = ring * nodeSpacing;

            const x = clusterCenterX + Math.cos(angle) * radius - 40;
            const y = clusterCenterY + Math.sin(angle) * radius - 40;
            positionedNodes.push({ ...node, position: { x, y } });
          }
        });
      }
    });

    // Place disconnected nodes on the right side
    if (disconnectedNodes.length > 0) {
      const disconnectedAreaX = mainAreaWidth + 50;
      const disconnectedSpacing = Math.min(140, (height - 100) / disconnectedNodes.length);
      const startY = (height - (disconnectedNodes.length - 1) * disconnectedSpacing) / 2;

      disconnectedNodes.forEach((node, index) => {
        positionedNodes.push({
          ...node,
          position: { x: disconnectedAreaX, y: startY + index * disconnectedSpacing - 40 }
        });
      });
    }
  } else {
    // Traditional grid layout with category grouping
    const mainAreaWidth = disconnectedNodes.length > 0 ? width * 0.75 : width;
    const nodeWidth = 280;
    const nodeHeight = 200;
    const padding = 30;

    let currentY = padding;

    // Layout each category as a row/group
    categories.forEach((category) => {
      const categoryNodes = nodesByCategory[category];
      const nodesPerRow = Math.min(4, categoryNodes.length);
      const rows = Math.ceil(categoryNodes.length / nodesPerRow);
      const cellWidth = (mainAreaWidth - 2 * padding) / nodesPerRow;

      categoryNodes.forEach((node, index) => {
        const col = index % nodesPerRow;
        const row = Math.floor(index / nodesPerRow);
        const x = padding + col * cellWidth + (cellWidth - nodeWidth) / 2;
        const y = currentY + row * (nodeHeight + 20);

        positionedNodes.push({ ...node, position: { x, y } });
      });

      currentY += rows * (nodeHeight + 20) + 40; // Add gap between categories
    });

    // Place disconnected nodes on the right side
    if (disconnectedNodes.length > 0) {
      const disconnectedAreaX = mainAreaWidth + 20;
      const spacing = Math.min(nodeHeight + 20, (height - 100) / disconnectedNodes.length);
      const startY = padding;

      disconnectedNodes.forEach((node, index) => {
        positionedNodes.push({
          ...node,
          position: { x: disconnectedAreaX, y: startY + index * spacing }
        });
      });
    }
  }

  return positionedNodes;
};

// Convert SchemaDesign to React Flow nodes/edges
const convertSchemaDesignToFlow = (
  design: SchemaDesign
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = design.nodes.map((node, index) => ({
    id: node.label,
    type: 'schemaNode',
    position: { x: 0, y: 0 }, // Will be calculated by layout
    data: {
      label: node.label,
      category: node.category || 'default',
      description: node.description,
      properties: node.properties || [],
      colors: getNodeColors(node.category),
    },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  }));

  const edges: Edge[] = design.relationships.map((rel, index) => {
    const sourceLabel = rel.from || rel.sourceLabel || '';
    const targetLabel = rel.to || rel.targetLabel || '';

    return {
      id: `edge-${index}-${sourceLabel}-${targetLabel}`,
      source: sourceLabel,
      target: targetLabel,
      type: 'schemaEdge',
      label: rel.type,
      data: {
        relationshipType: rel.type,
        description: rel.description,
        properties: rel.properties,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#64748b',
      },
      style: {
        stroke: '#64748b',
        strokeWidth: 2,
      },
      animated: false,
    };
  });

  // Apply layout — dagre layered layout (spreads across the full width).
  const layoutedNodes = layoutWithDagre(nodes, edges);

  return { nodes: layoutedNodes, edges };
};

// Convert SchemaPreview to React Flow nodes/edges
const convertSchemaPreviewToFlow = (
  preview: SchemaPreview
): { nodes: Node[]; edges: Edge[] } => {
  // Create a lookup map from table identifiers to table_id
  // Relationships may reference tables by name, node_label, or ID
  const tableNameToId: Record<string, string> = {};
  preview.tables.forEach(table => {
    // Map by table_name
    tableNameToId[table.table_name] = table.table_id;
    // Also map by node_label if different
    if (table.node_label && table.node_label !== table.table_name) {
      tableNameToId[table.node_label] = table.table_id;
    }
    // Also map ID to itself (for local relationships that use IDs directly)
    tableNameToId[table.table_id] = table.table_id;
  });

  // Check if this is a KG-First schema (has kg_area_id on tables)
  const isKGFirst = preview.tables.some((t: any) => t.kg_area_id);

  const nodes: Node[] = preview.tables.map((table, index) => {
    // Cast to access KG-First specific fields
    const kgTable = table as any;

    return {
      id: table.table_id,
      type: isKGFirst ? 'kgEntity' : 'schemaNode', // Use circular nodes for KG-First
      position: { x: 0, y: 0 }, // Will be calculated by layout
      data: {
        // Use semantic node_label if available, fall back to table_name
        label: table.node_label || table.table_name,
        // Show source table name as subtitle when different from node_label
        sourceTableName: table.node_label && table.node_label !== table.table_name
          ? table.table_name
          : undefined,
        category: table.module_name,
        description: kgTable.description || `Module: ${table.module_name}`,
        properties: table.columns.map(col => ({
          name: col.column_name,
          type: col.data_type,
          isPrimaryKey: col.is_primary_key,
          isForeignKey: col.is_foreign_key,
        })),
        isCentral: table.is_central,
        connectionCount: table.connection_count,
        colors: table.is_central
          ? getNodeColors('central')
          : getNodeColors(table.module_name),
        // KG-First specific fields
        kgAreaId: kgTable.kg_area_id,
        kgAreaName: kgTable.kg_area_name,
        isMasterData: kgTable.is_master_data,
        isTransactional: kgTable.is_transactional,
        isAnalytical: kgTable.is_analytical,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
  });

  // Convert table names to IDs for edge connections
  const edges: Edge[] = preview.relationships
    .filter(rel => {
      // Only include edges where both source and target tables exist
      const sourceId = tableNameToId[rel.source_table];
      const targetId = tableNameToId[rel.target_table];
      return sourceId && targetId;
    })
    .map((rel, index) => {
      // Cast to access KG-First specific fields
      const kgRel = rel as any;

      return {
        id: rel.relationship_id || `edge-${index}`,
        source: tableNameToId[rel.source_table], // Map name to ID
        target: tableNameToId[rel.target_table], // Map name to ID
        type: 'schemaEdge',
        label: rel.relationship_type,
        data: {
          relationshipType: rel.relationship_type,
          cardinality: rel.cardinality,
          sourceColumn: rel.source_column,
          targetColumn: rel.target_column,
          // KG-First specific fields
          businessProcess: kgRel.business_process,
          derivedFromFK: kgRel.derived_from_fk,
          description: kgRel.description,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isKGFirst ? '#10b981' : '#64748b', // Green for KG-First semantic relationships
        },
        style: {
          stroke: isKGFirst ? '#10b981' : '#64748b',
          strokeWidth: 2,
        },
        animated: false,
      };
    });

  // Apply layout — dagre layered layout (spreads across the full width).
  const layoutedNodes = layoutWithDagre(nodes, edges);

  return { nodes: layoutedNodes, edges };
};

export const ReactFlowSchemaGraph: React.FC<ReactFlowSchemaGraphProps> = ({
  schemaDesign,
  schemaPreview,
  onNodeClick,
  onEdgeClick,
  onRelationshipAdd,
  onNodeDelete,
  onEdgeDelete,
  onNodeAdd,
  onNodeEdit,
  onEdgeEdit,
  editable = true,
  height = 600,
  className = '',
}) => {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<{ id: string; label: string } | null>(null);

  // State for add relationship modal
  const [isAddRelationshipModalOpen, setIsAddRelationshipModalOpen] = useState(false);

  // State for add node modal
  const [isAddNodeModalOpen, setIsAddNodeModalOpen] = useState(false);

  // State for edit node modal
  const [isEditNodeModalOpen, setIsEditNodeModalOpen] = useState(false);
  const [editingNode, setEditingNode] = useState<{
    nodeId: string;
    label: string;
    category?: string;
    description?: string;
    properties?: { name: string; type: string; is_primary_key?: boolean }[];
  } | null>(null);

  // State for edit relationship modal
  const [isEditRelationshipModalOpen, setIsEditRelationshipModalOpen] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState<{
    edgeId: string;
    relationshipType: string;
    sourceLabel: string;
    targetLabel: string;
    cardinality?: string;
    description?: string;
  } | null>(null);
  const [pendingConnection, setPendingConnection] = useState<{
    source: string;
    target: string;
    sourceLabel: string;
    targetLabel: string;
  } | null>(null);

  // State for delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    type: 'node' | 'edge';
    id: string;
    label: string;
  } | null>(null);

  // Convert data to React Flow format
  const { initialNodes, initialEdges } = useMemo(() => {
    if (schemaDesign && schemaDesign.nodes && schemaDesign.nodes.length > 0) {
      const { nodes, edges } = convertSchemaDesignToFlow(schemaDesign);
      return { initialNodes: nodes, initialEdges: edges };
    }
    if (schemaPreview && schemaPreview.tables && schemaPreview.tables.length > 0) {
      const { nodes, edges } = convertSchemaPreviewToFlow(schemaPreview);
      return { initialNodes: nodes, initialEdges: edges };
    }
    return { initialNodes: [], initialEdges: [] };
  }, [schemaDesign, schemaPreview]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Sync state when data changes (React Flow hooks only initialize once)
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Extract used KG areas/modules for dynamic legend
  const { usedAreas, hasCentralNodes } = useMemo(() => {
    const areas = new Set<string>();
    let hasCentral = false;

    for (const node of nodes) {
      const data = node.data as any;
      // Check for KG area name
      if (data?.kgAreaName) {
        areas.add(data.kgAreaName);
      }
      // Check for module/category name
      if (data?.category) {
        areas.add(data.category);
      }
      // Check if central
      if (data?.isCentral || data?.isMasterData) {
        hasCentral = true;
      }
    }

    return {
      usedAreas: Array.from(areas),
      hasCentralNodes: hasCentral,
    };
  }, [nodes]);

  // ---- Refinement state: layout mode, hover-highlight, area filter, search, export ----
  const containerRef = useRef<HTMLDivElement>(null);
  const [rfInstance, setRfInstance] = useState<any>(null);
  const [layoutMode, setLayoutMode] = useState<'grouped' | 'force'>('grouped');
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [hiddenAreas, setHiddenAreas] = useState<Set<string>>(new Set());
  const [showFilter, setShowFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Undirected adjacency for hover neighbor-highlight.
  const adjacency = useMemo(
    () => buildAdjacency(edges.map((e) => ({ source: e.source, target: e.target }))),
    [edges]
  );

  // Nodes matching the current search query (by label).
  const searchMatches = useMemo(
    () =>
      matchNodeIds(
        searchQuery,
        nodes.map((n) => ({ id: n.id, label: ((n.data as any)?.label as string) || n.id }))
      ),
    [searchQuery, nodes]
  );

  const getNodeArea = useCallback((node: Node): string => {
    const data = node.data as any;
    return data?.kgAreaName || data?.category || data?.module_name || 'Other';
  }, []);

  const toggleArea = useCallback((area: string) => {
    setHiddenAreas((prev) => {
      const next = new Set(prev);
      if (next.has(area)) next.delete(area);
      else next.add(area);
      return next;
    });
  }, []);

  // Node ids visible after the area filter (drives node + edge visibility).
  const visibleNodeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const node of nodes) {
      if (!hiddenAreas.has(getNodeArea(node))) ids.add(node.id);
    }
    return ids;
  }, [nodes, hiddenAreas, getNodeArea]);

  // Highlight set = hovered node + neighbors (null when nothing is hovered).
  const highlightSet = useMemo(
    () => (hoveredNodeId ? neighborhoodOf(hoveredNodeId, adjacency) : null),
    [hoveredNodeId, adjacency]
  );

  // Nodes decorated with filter/hover/search styling (identity + position unchanged).
  const displayNodes = useMemo(() => {
    return nodes
      .filter((n) => visibleNodeIds.has(n.id))
      .map((n) => {
        const dimmed =
          (!!highlightSet && !highlightSet.has(n.id)) ||
          (searchMatches.size > 0 && !searchMatches.has(n.id));
        const isMatch = searchMatches.has(n.id);
        return {
          ...n,
          style: {
            ...n.style,
            opacity: dimmed ? DIMMED_OPACITY : 1,
            outline: isMatch ? '3px solid #f59e0b' : undefined,
            outlineOffset: isMatch ? '2px' : undefined,
            transition: 'opacity 150ms ease',
          },
        };
      });
  }, [nodes, visibleNodeIds, highlightSet, searchMatches]);

  // Edges hidden when an endpoint is filtered out; dimmed when not incident to
  // the hovered node.
  const displayEdges = useMemo(() => {
    return edges
      .filter((e) => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target))
      .map((e) => {
        const dimmed = !!highlightSet && !(highlightSet.has(e.source) && highlightSet.has(e.target));
        return {
          ...e,
          data: { ...(e.data as any), dimmed },
          style: { ...e.style, opacity: dimmed ? DIMMED_OPACITY : 1 },
        };
      });
  }, [edges, visibleNodeIds, highlightSet]);

  const onNodeMouseEnter = useCallback((_event: React.MouseEvent, node: Node) => {
    setHoveredNodeId(node.id);
  }, []);
  const onNodeMouseLeave = useCallback(() => setHoveredNodeId(null), []);

  // Apply the selected layout algorithm to the current nodes.
  const applyLayoutMode = useCallback(
    (mode: 'grouped' | 'force') => {
      if (nodes.length === 0) return;
      let positioned: Node[];
      if (mode === 'force') {
        const positions = computeForceLayout(
          nodes.map((n) => ({ id: n.id })),
          edges.map((e) => ({ source: e.source, target: e.target })),
          { collideRadius: nodes.some((n) => n.type === 'schemaNode') ? 170 : 90 }
        );
        positioned = nodes.map((n) => ({ ...n, position: positions[n.id] || n.position }));
      } else {
        // 'grouped' → dagre layered layout (fills the full width in clean layers).
        positioned = layoutWithDagre(
          nodes,
          edges.map((e) => ({ source: e.source, target: e.target }))
        );
      }
      setNodes(positioned);
      setLayoutMode(mode);
      window.requestAnimationFrame(() => rfInstance?.fitView?.({ padding: 0.2, duration: 400 }));
    },
    [nodes, edges, setNodes, rfInstance]
  );

  // Locate + center the current search matches.
  const handleSearchLocate = useCallback(() => {
    if (!rfInstance || searchMatches.size === 0) return;
    const matched = nodes.filter((n) => searchMatches.has(n.id)).map((n) => ({ id: n.id }));
    rfInstance.fitView({ nodes: matched, duration: 500, padding: 0.4 });
  }, [rfInstance, searchMatches, nodes]);

  // Handle node click
  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      setSelectedNode(node.id);
      setSelectedEdge(null); // Clear edge selection
      if (onNodeClick) {
        if (schemaDesign) {
          const schemaNode = schemaDesign.nodes.find(n => n.label === node.id);
          if (schemaNode) onNodeClick(node.id, schemaNode);
        } else if (schemaPreview) {
          const previewTable = schemaPreview.tables.find(t => t.table_id === node.id);
          if (previewTable) onNodeClick(node.id, previewTable);
        }
      }
    },
    [onNodeClick, schemaDesign, schemaPreview]
  );

  // Handle edge click
  const handleEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      setSelectedNode(null); // Clear node selection
      // Get the relationship label for the edge
      const edgeLabel = (edge.data as any)?.label || edge.id;
      setSelectedEdge({ id: edge.id, label: edgeLabel });

      if (onEdgeClick) {
        if (schemaDesign) {
          const schemaRel = schemaDesign.relationships.find(
            r => (r.from || r.sourceLabel) === edge.source && (r.to || r.targetLabel) === edge.target
          );
          if (schemaRel) onEdgeClick(edge.id, schemaRel);
        } else if (schemaPreview) {
          const previewRel = schemaPreview.relationships.find(r => r.relationship_id === edge.id);
          if (previewRel) onEdgeClick(edge.id, previewRel);
        }
      }
    },
    [onEdgeClick, schemaDesign, schemaPreview]
  );

  // Handle clicking on background (deselect)
  const handlePaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  // Re-layout nodes using the currently selected layout mode
  const handleRelayout = useCallback(() => {
    applyLayoutMode(layoutMode);
  }, [applyLayoutMode, layoutMode]);

  // Handle connection attempt (drag from one node to another)
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!editable || !connection.source || !connection.target) return;

      // Find the node labels for display
      const sourceNode = nodes.find(n => n.id === connection.source);
      const targetNode = nodes.find(n => n.id === connection.target);

      const sourceLabel = (sourceNode?.data as any)?.label || connection.source;
      const targetLabel = (targetNode?.data as any)?.label || connection.target;

      // Store pending connection and open modal
      setPendingConnection({
        source: connection.source,
        target: connection.target,
        sourceLabel,
        targetLabel,
      });
      setIsAddRelationshipModalOpen(true);
    },
    [editable, nodes]
  );

  // Handle confirmation from add relationship modal
  const handleAddRelationshipConfirm = useCallback(
    (relationshipData: NewRelationshipData) => {
      if (!pendingConnection) return;

      // Create new edge
      const newEdge: Edge = {
        id: `edge-new-${Date.now()}`,
        source: pendingConnection.source,
        target: pendingConnection.target,
        type: 'schemaEdge',
        label: relationshipData.relationshipType,
        data: {
          relationshipType: relationshipData.relationshipType,
          cardinality: relationshipData.cardinality,
          description: relationshipData.description,
          isNew: true, // Mark as newly added
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#10b981',
        },
        style: {
          stroke: '#10b981',
          strokeWidth: 2,
        },
        animated: true, // Animate new edges to make them visible
      };

      // Add edge to state
      setEdges(eds => [...eds, newEdge]);

      // Notify parent component
      if (onRelationshipAdd) {
        onRelationshipAdd({
          sourceId: pendingConnection.source,
          targetId: pendingConnection.target,
          relationshipType: relationshipData.relationshipType,
          cardinality: relationshipData.cardinality,
          description: relationshipData.description,
        });
      }

      // Close modal and clear pending connection
      setIsAddRelationshipModalOpen(false);
      setPendingConnection(null);
    },
    [pendingConnection, setEdges, onRelationshipAdd]
  );

  // Handle modal close
  const handleAddRelationshipClose = useCallback(() => {
    setIsAddRelationshipModalOpen(false);
    setPendingConnection(null);
  }, []);

  // Handle delete confirmation request
  const handleDeleteRequest = useCallback((type: 'node' | 'edge', id: string, label: string) => {
    setDeleteConfirmation({ type, id, label });
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(() => {
    if (!deleteConfirmation) return;

    if (deleteConfirmation.type === 'node') {
      // Remove the node and all connected edges
      setNodes((nds) => nds.filter((n) => n.id !== deleteConfirmation.id));
      setEdges((eds) => eds.filter(
        (e) => e.source !== deleteConfirmation.id && e.target !== deleteConfirmation.id
      ));
      // Notify parent
      if (onNodeDelete) {
        onNodeDelete(deleteConfirmation.id);
      }
    } else {
      // Remove the edge
      setEdges((eds) => eds.filter((e) => e.id !== deleteConfirmation.id));
      // Notify parent
      if (onEdgeDelete) {
        onEdgeDelete(deleteConfirmation.id);
      }
    }

    setDeleteConfirmation(null);
    setSelectedNode(null);
  }, [deleteConfirmation, setNodes, setEdges, onNodeDelete, onEdgeDelete]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmation(null);
  }, []);

  // Handle add node modal
  const handleAddNodeOpen = useCallback(() => {
    setIsAddNodeModalOpen(true);
  }, []);

  const handleAddNodeClose = useCallback(() => {
    setIsAddNodeModalOpen(false);
  }, []);

  const handleAddNodeConfirm = useCallback(
    (nodeData: NewNodeData) => {
      // Create a new node for React Flow
      const newNodeId = nodeData.nodeLabel;
      const colors = getNodeColors(nodeData.category);

      // Calculate position - place new node to the right of existing nodes
      const maxX = Math.max(...nodes.map((n) => n.position.x), 0) + 200;
      const avgY = nodes.length > 0
        ? nodes.reduce((sum, n) => sum + n.position.y, 0) / nodes.length
        : 200;

      const newNode: Node = {
        id: newNodeId,
        type: 'kgEntity',
        position: { x: maxX, y: avgY },
        data: {
          label: nodeData.nodeLabel,
          kgAreaName: nodeData.category,
          category: nodeData.category,
          description: nodeData.description,
          properties: nodeData.properties.map((p) => ({
            name: p.name,
            type: p.type,
            is_primary_key: p.isPrimaryKey,
          })),
          colors,
          isMasterData: false,
          isTransactional: true,
        },
      };

      // Add to React Flow nodes
      setNodes((nds) => [...nds, newNode]);

      // Notify parent
      if (onNodeAdd) {
        onNodeAdd(nodeData);
      }

      setIsAddNodeModalOpen(false);
    },
    [nodes, setNodes, onNodeAdd]
  );

  // Get list of existing node labels for duplicate checking
  const existingNodeLabels = useMemo(() => {
    return nodes.map((n) => (n.data as any)?.label || n.id);
  }, [nodes]);

  // Handle edit node modal
  const handleEditNodeOpen = useCallback(
    (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        const data = node.data as any;
        setEditingNode({
          nodeId: node.id,
          label: data?.label || node.id,
          category: data?.kgAreaName || data?.category || data?.module_name,
          description: data?.description,
          properties: data?.properties || [],
        });
        setIsEditNodeModalOpen(true);
      }
    },
    [nodes]
  );

  const handleEditNodeClose = useCallback(() => {
    setIsEditNodeModalOpen(false);
    setEditingNode(null);
  }, []);

  const handleEditNodeConfirm = useCallback(
    (editData: EditNodeData) => {
      // Update the node in React Flow
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === editData.nodeId) {
            const existingData = node.data as any;
            const colors = getNodeColors(editData.category);
            return {
              ...node,
              data: {
                ...existingData,
                label: editData.label,
                kgAreaName: editData.category,
                category: editData.category,
                description: editData.description,
                properties: editData.properties.map((p) => ({
                  name: p.name,
                  type: p.type,
                  is_primary_key: p.isPrimaryKey,
                })),
                colors,
              },
            };
          }
          return node;
        })
      );

      // Notify parent
      if (onNodeEdit) {
        onNodeEdit(editData);
      }

      setIsEditNodeModalOpen(false);
      setEditingNode(null);
    },
    [setNodes, onNodeEdit]
  );

  // Handle edit relationship modal
  const handleEditRelationshipOpen = useCallback(
    (edgeId: string) => {
      const edge = edges.find((e) => e.id === edgeId);
      if (edge) {
        const data = edge.data as any;
        const sourceNode = nodes.find((n) => n.id === edge.source);
        const targetNode = nodes.find((n) => n.id === edge.target);
        setEditingRelationship({
          edgeId: edge.id,
          relationshipType: data?.label || data?.relationshipType || '',
          sourceLabel: (sourceNode?.data as any)?.label || edge.source,
          targetLabel: (targetNode?.data as any)?.label || edge.target,
          cardinality: data?.cardinality,
          description: data?.description,
        });
        setIsEditRelationshipModalOpen(true);
      }
    },
    [edges, nodes]
  );

  const handleEditRelationshipClose = useCallback(() => {
    setIsEditRelationshipModalOpen(false);
    setEditingRelationship(null);
  }, []);

  const handleEditRelationshipConfirm = useCallback(
    (editData: EditRelationshipData) => {
      // Update the edge in React Flow
      setEdges((eds) =>
        eds.map((edge) => {
          if (edge.id === editData.edgeId) {
            const existingData = edge.data as any;
            return {
              ...edge,
              label: editData.relationshipType,
              data: {
                ...existingData,
                label: editData.relationshipType,
                relationshipType: editData.relationshipType,
                cardinality: editData.cardinality,
                description: editData.description,
              },
            };
          }
          return edge;
        })
      );

      // Notify parent
      if (onEdgeEdit) {
        onEdgeEdit(editData);
      }

      setIsEditRelationshipModalOpen(false);
      setEditingRelationship(null);

      // Update selected edge label
      if (selectedEdge && selectedEdge.id === editData.edgeId) {
        setSelectedEdge({
          id: editData.edgeId,
          label: editData.relationshipType,
        });
      }
    },
    [setEdges, onEdgeEdit, selectedEdge]
  );

  // Export the graph canvas as PNG (excludes the UI overlays)
  const handleExport = useCallback(() => {
    void exportElementToPng(containerRef.current, 'schema-graph.png', {
      filter: (node) => {
        const cls = (node as HTMLElement).classList;
        if (!cls) return true;
        return !(
          cls.contains('react-flow__panel') ||
          cls.contains('react-flow__minimap') ||
          cls.contains('react-flow__controls') ||
          cls.contains('react-flow__attribution')
        );
      },
    });
  }, []);

  if (initialNodes.length === 0) {
    return (
      <div
        className={`flex items-center justify-center bg-neutral-50 border border-neutral-200 rounded-lg ${className}`}
        style={{ height }}
      >
        <div className="text-center text-neutral-500">
          <p className="font-medium">No schema data available</p>
          <p className="text-sm">Generate a schema to see the visualization</p>
        </div>
      </div>
    );
  }

  // Ensure height is properly set - convert number to px, use string as-is
  const containerHeight = typeof height === 'number' ? `${height}px` : (height || '600px');

  return (
    <>
      <div
        ref={containerRef}
        className={`bg-white border border-neutral-200 rounded-lg overflow-hidden ${className}`}
        style={{ height: containerHeight, width: '100%' }}
      >
        <ReactFlow
          nodes={displayNodes}
          edges={displayEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick}
          onNodeMouseEnter={onNodeMouseEnter}
          onNodeMouseLeave={onNodeMouseLeave}
          onPaneClick={handlePaneClick}
          onConnect={editable ? handleConnect : undefined}
          onInit={setRfInstance}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          proOptions={{ hideAttribution: true }}
          deleteKeyCode={null}
          connectionLineStyle={{ stroke: '#10b981', strokeWidth: 2 }}
          connectionLineType={ConnectionLineType.SmoothStep}
        >
          <Background color="#e5e7eb" gap={20} size={1} />
          <Controls showInteractive={editable} />
          <MiniMap
            nodeColor={(node) => {
              const data = node.data as { colors?: { border?: string } } | undefined;
              return data?.colors?.border || '#64748b';
            }}
            maskColor="rgba(255, 255, 255, 0.8)"
            style={{ background: '#f8fafc' }}
          />
          <Panel position="top-right" className="flex max-w-[calc(100%-1rem)] flex-wrap items-center justify-end gap-2">
            {/* Search box — kept in the same wrapping row as the actions so it never
                overlaps them on a narrow canvas (was a separate top-center panel). */}
            <div className="flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2 py-1 shadow-sm">
              <Search className="h-4 w-4 text-neutral-400" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchLocate();
                }}
                placeholder="Search nodes…"
                className="w-32 bg-transparent text-sm outline-none sm:w-40"
              />
              {searchQuery && (
                <>
                  <span className="text-xs text-neutral-400">{searchMatches.size}</span>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="rounded p-0.5 hover:bg-neutral-100"
                    title="Clear search"
                  >
                    <X className="h-3 w-3 text-neutral-400" />
                  </button>
                </>
              )}
            </div>

            {/* Layout mode toggle */}
            <div className="flex items-center overflow-hidden rounded-lg border border-neutral-200 bg-white">
              <button
                onClick={() => applyLayoutMode('grouped')}
                title="Layered layout (dagre — spreads across the full width)"
                className={`flex items-center gap-1 px-2 py-1.5 text-xs ${layoutMode === 'grouped' ? 'bg-blue-50 text-blue-700' : 'text-neutral-600 hover:bg-neutral-50'}`}
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Layered
              </button>
              <button
                onClick={() => applyLayoutMode('force')}
                title="Force-directed layout"
                className={`flex items-center gap-1 border-l border-neutral-200 px-2 py-1.5 text-xs ${layoutMode === 'force' ? 'bg-blue-50 text-blue-700' : 'text-neutral-600 hover:bg-neutral-50'}`}
              >
                <Share2 className="h-3.5 w-3.5" /> Force
              </button>
            </div>

            {usedAreas.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilter((v) => !v)}
                title="Filter by area"
                className={hiddenAreas.size > 0 ? 'border-blue-300 bg-blue-50 text-blue-700' : ''}
              >
                <Filter className="h-4 w-4" />
                {hiddenAreas.size > 0 ? `Filtered (${hiddenAreas.size})` : 'Filter'}
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={handleExport} title="Export as PNG">
              <Download className="h-4 w-4" />
            </Button>

            <Button variant="outline" size="sm" onClick={handleRelayout} title="Re-layout nodes">
              <RefreshCw className="h-4 w-4" />
            </Button>

            {editable && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddNodeOpen}
                title="Add new node"
                className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
              >
                <Plus className="mr-1 h-4 w-4" />
                Add Node
              </Button>
            )}
          </Panel>

          {/* Area filter dropdown */}
          {showFilter && usedAreas.length > 0 && (
            <Panel position="top-right" className="mr-2 mt-14">
              <div className="max-h-64 w-56 overflow-auto rounded-lg border border-neutral-200 bg-white p-2 shadow-lg">
                <div className="mb-1 px-1 text-xs font-semibold text-neutral-600">Filter by area</div>
                {usedAreas.map((area) => (
                  <label
                    key={area}
                    className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-neutral-50"
                  >
                    <input
                      type="checkbox"
                      checked={!hiddenAreas.has(area)}
                      onChange={() => toggleArea(area)}
                    />
                    <span className="truncate">{area}</span>
                  </label>
                ))}
                {hiddenAreas.size > 0 && (
                  <button
                    onClick={() => setHiddenAreas(new Set())}
                    className="mt-1 px-1 text-xs text-blue-600 hover:underline"
                  >
                    Show all
                  </button>
                )}
              </div>
            </Panel>
          )}

          {/* Large-graph guard banner */}
          {nodes.length > 250 && (
            <Panel position="bottom-center" className="mb-10">
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 shadow-sm">
                <AlertTriangle className="h-3.5 w-3.5" />
                Large graph ({nodes.length} nodes) — use Filter or Search for smoother interaction.
              </div>
            </Panel>
          )}

          {/* Edit Mode Indicator */}
          {editable && (
            <Panel position="top-left" className="ml-2 mt-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 text-xs text-emerald-700 flex items-center gap-2">
                <Plus className="w-3 h-3" />
                <span>Drag between nodes to add relationship</span>
              </div>
            </Panel>
          )}

          {/* Selection Actions Panel */}
          {editable && (selectedNode || selectedEdge) && (
            <Panel position="bottom-right" className="mb-2 mr-2">
              <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-[200px]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-neutral-700">
                    {selectedNode ? 'Node Selected' : 'Relationship Selected'}
                  </div>
                  <button
                    onClick={() => {
                      setSelectedNode(null);
                      setSelectedEdge(null);
                    }}
                    className="p-1 hover:bg-neutral-100 rounded"
                  >
                    <X className="w-3 h-3 text-neutral-400" />
                  </button>
                </div>
                <div className="text-xs text-neutral-500 mb-3 truncate">
                  {selectedNode || selectedEdge?.label}
                </div>
                <div className="space-y-2">
                  {/* Edit button for nodes */}
                  {selectedNode && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditNodeOpen(selectedNode)}
                      className="w-full text-amber-600 border-amber-200 hover:bg-amber-50 hover:border-amber-300"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Edit Properties
                    </Button>
                  )}
                  {/* Edit button for relationships */}
                  {selectedEdge && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditRelationshipOpen(selectedEdge.id)}
                      className="w-full text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Edit Relationship
                    </Button>
                  )}
                  {/* Delete button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (selectedNode) {
                        const node = nodes.find(n => n.id === selectedNode);
                        const label = (node?.data as any)?.label || selectedNode;
                        handleDeleteRequest('node', selectedNode, label);
                      } else if (selectedEdge) {
                        handleDeleteRequest('edge', selectedEdge.id, selectedEdge.label);
                      }
                    }}
                    className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Panel>
          )}

          {/* Color Legend Panel - shows only colors actually used in schema */}
          <Panel position="bottom-left" className="mb-2 ml-2">
            <SchemaLegend
              defaultExpanded={false}
              usedAreas={usedAreas}
              hasCentralNodes={hasCentralNodes}
            />
          </Panel>
        </ReactFlow>
      </div>

      {/* Add Relationship Modal */}
      <AddRelationshipModal
        isOpen={isAddRelationshipModalOpen}
        onClose={handleAddRelationshipClose}
        onConfirm={handleAddRelationshipConfirm}
        sourceNode={pendingConnection ? { id: pendingConnection.source, label: pendingConnection.sourceLabel } : null}
        targetNode={pendingConnection ? { id: pendingConnection.target, label: pendingConnection.targetLabel } : null}
      />

      {/* Add Node Modal */}
      <AddNodeModal
        isOpen={isAddNodeModalOpen}
        onClose={handleAddNodeClose}
        onConfirm={handleAddNodeConfirm}
        existingNodes={existingNodeLabels}
      />

      {/* Edit Node Modal */}
      <EditNodeModal
        isOpen={isEditNodeModalOpen}
        onClose={handleEditNodeClose}
        onConfirm={handleEditNodeConfirm}
        nodeData={editingNode}
      />

      {/* Edit Relationship Modal */}
      <EditRelationshipModal
        isOpen={isEditRelationshipModalOpen}
        onClose={handleEditRelationshipClose}
        onConfirm={handleEditRelationshipConfirm}
        relationshipData={editingRelationship}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleDeleteCancel}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-red-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">
                    Delete {deleteConfirmation.type === 'node' ? 'Node' : 'Relationship'}
                  </h2>
                </div>
              </div>
              <button
                onClick={handleDeleteCancel}
                className="p-2 hover:bg-red-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-neutral-500" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-neutral-600">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-neutral-800">
                  {deleteConfirmation.label}
                </span>
                ?
              </p>
              {deleteConfirmation.type === 'node' && (
                <p className="text-sm text-neutral-500 mt-2">
                  This will also remove all relationships connected to this node.
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50">
              <Button variant="outline" onClick={handleDeleteCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReactFlowSchemaGraph;
